import { SettingOutlined } from '@ant-design/icons';
import { Button, Col, Form, Modal, Row, Spin, Switch } from 'antd';
import React, { CSSProperties, useCallback, useContext, useEffect, useRef, useState } from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import { SizeMe } from 'react-sizeme';
import { apiClient } from '../../utils';
import {
  IntegrationsWidget,
  SavedSearchesWidget,
  StatisticsWidget,
  WelcomeWidget,
  WorkloadWidget,
  DataQualityWidget,
  DataSetsWidget,
} from './widgets';
import { UserContext } from '../contexts';

export function HomePage() {
  const { user } = useContext(UserContext);

  const [isGuest, setIsGuest] = useState(true);

  const [widgetsLoading, setWidgetsLoading] = useState(false);
  const [widgetsLayout, setWidgetsLayout] = useState<Layout[]>();
  const [widgetsModalOpen, setWidgetsModalOpen] = useState(false);
  const ignoreFirstLayoutChange = useRef(true);

  const defaultLayout = useCallback(
    () => [
      { i: 'welcome', x: 0, y: 0, w: 1, h: 4 },
      { i: 'dataSets', x: 1, y: 0, w: 1, h: 4 },
      { i: 'savedSearches', x: 2, y: 0, w: 1, h: 4 },
      { i: 'statistics', x: 0, y: 3, w: 1, h: 4 },
      { i: 'workload', x: 1, y: 3, w: 1, h: 4 },
      { i: 'dataQuality', x: 2, y: 3, w: 1, h: 4 },
    ],
    [],
  );

  const guestDefaultLayout = useCallback(
    () => [
      { i: 'welcome', x: 0, y: 0, w: 3, h: 2 },
      { i: 'dataSets', x: 0, y: 3, w: 2, h: 4 },
      { i: 'dataQuality', x: 2, y: 3, w: 1, h: 4 },
    ],
    [],
  );

  const postWidgetsLayout = (newWidgetsLayout: Layout[]) => {
    localStorage.setItem('mdm_widgets_layout', JSON.stringify(newWidgetsLayout));
    apiClient
      .put('users/me/settings/widgets', { widgetsLayout: JSON.stringify(newWidgetsLayout) })
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        setWidgetsLoading(false);
      });
  };

  const loadWidgetsLayout = useCallback(() => {
    const cachedLayout = localStorage.getItem('mdm_widgets_layout');
    if (cachedLayout) {
      setWidgetsLayout(JSON.parse(cachedLayout) as Layout[]);
      return;
    }

    setWidgetsLoading(true);
    apiClient
      .get('users/me/settings/widgets')
      .then(response => {
        const layoutFromServer = (response.data as Layout[]) ?? defaultLayout();
        setWidgetsLayout(layoutFromServer);
        localStorage.setItem('mdm_widgets_layout', JSON.stringify(layoutFromServer));
      })
      .catch(() => {
        const fallbackLayout = defaultLayout();
        setWidgetsLayout(fallbackLayout);
        localStorage.setItem('mdm_widgets_layout', JSON.stringify(fallbackLayout));
      })
      .finally(() => {
        setWidgetsLoading(false);
      });
  }, [defaultLayout]);

  useEffect(() => {
    if (user) {
      const isLoggedAsGuest = user.email.toLowerCase() === 'misafir';
      setIsGuest(isLoggedAsGuest);
      if (isLoggedAsGuest) {
        setWidgetsLayout(guestDefaultLayout());
      } else {
        loadWidgetsLayout();
      }
    }
  }, [user, guestDefaultLayout, loadWidgetsLayout]);

  const onLayoutChange = (newWidgetsLayout: Layout[]) => {
    if (isGuest) {
      return;
    }

    if (ignoreFirstLayoutChange.current) {
      ignoreFirstLayoutChange.current = false;
      return;
    }

    setWidgetsLayout(newWidgetsLayout);
    localStorage.setItem('mdm_widgets_layout', JSON.stringify(newWidgetsLayout));
    postWidgetsLayout(newWidgetsLayout);
  };

  const openWidgetsModal = () => {
    setWidgetsModalOpen(true);
  };

  const closeWidgetsModal = () => {
    setWidgetsModalOpen(false);
  };

  const onWidgetsSwitchChange = (key: string, active: boolean) => {
    if (active) {
      const newLayout = widgetsLayout ? [...widgetsLayout] : ([] as Layout[]);
      const firstColumnItemsHeight =
        newLayout
          .filter(w => w.x === 0)
          .map(w => w.h)
          .reduce((a, b) => a + b, 0) || 0;
      newLayout.push({
        i: key,
        x: 0,
        y: firstColumnItemsHeight,
        w: 1,
        h: 4,
      });
      setWidgetsLayout(newLayout);
    } else {
      const newLayout = widgetsLayout?.filter(w => w.i !== key);
      setWidgetsLayout(newLayout);
    }
  };

  const widgetStyle: CSSProperties = {
    border: '1px solid #ddd',
    borderRadius: '5px',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  };

  return (
    <Spin spinning={widgetsLoading} style={{ width: '100%', paddingTop: '5rem' }}>
      {widgetsLayout && (
        <>
          <SizeMe>
            {({ size }) => (
              <GridLayout
                className="layout"
                layout={widgetsLayout}
                cols={3}
                rowHeight={70}
                width={size.width || 1200}
                margin={[20, 20]}
                onLayoutChange={onLayoutChange}
              >
                {widgetsLayout.some(w => w.i === 'welcome') && (
                  <div style={widgetStyle} key="welcome">
                    <WelcomeWidget />
                  </div>
                )}
                {widgetsLayout.some(w => w.i === 'statistics') && (
                  <div style={widgetStyle} key="statistics">
                    <StatisticsWidget />
                  </div>
                )}
                {widgetsLayout.some(w => w.i === 'integrations') && (
                  <div style={widgetStyle} key="integrations">
                    <IntegrationsWidget />
                  </div>
                )}
                {widgetsLayout.some(w => w.i === 'savedSearches') && (
                  <div style={widgetStyle} key="savedSearches">
                    <SavedSearchesWidget />
                  </div>
                )}
                {widgetsLayout.some(w => w.i === 'workload') && (
                  <div style={widgetStyle} key="workload">
                    <WorkloadWidget />
                  </div>
                )}
                {widgetsLayout.some(w => w.i === 'dataQuality') && (
                  <div style={widgetStyle} key="dataQuality">
                    <DataQualityWidget />
                  </div>
                )}
                {widgetsLayout.some(w => w.i === 'dataSets') && (
                  <div style={widgetStyle} key="dataSets">
                    <DataSetsWidget />
                  </div>
                )}
              </GridLayout>
            )}
          </SizeMe>
          {!isGuest && (
            <Row justify="end" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
              <Col span={2} style={{ textAlign: 'right', padding: '1rem' }}>
                <Button type="default" shape="circle" icon={<SettingOutlined />} onClick={openWidgetsModal} />
              </Col>
            </Row>
          )}
          <Modal
            title="Ana sayfa widget seçenekleri"
            visible={widgetsModalOpen}
            footer={null}
            onCancel={closeWidgetsModal}
            destroyOnClose={true}
          >
            <WidgetsSwitches widgetsLayout={widgetsLayout} onChange={onWidgetsSwitchChange} />
          </Modal>
        </>
      )}
    </Spin>
  );
}

function WidgetsSwitches(props: { widgetsLayout: Layout[]; onChange: (key: string, active: boolean) => void }) {
  const widgetList = [
    { key: 'welcome', name: 'Hoşgeldiniz' },
    { key: 'dataSets', name: 'Veri setleri' },
    { key: 'statistics', name: 'İstatistikler' },
    { key: 'integrations', name: 'Aktarımlar' },
    { key: 'savedSearches', name: 'Kaydedilmiş aramalar' },
    { key: 'workload', name: 'Bekleyen görevler' },
    { key: 'dataQuality', name: 'Veri kalitesi' },
  ];

  const onChange = (active: boolean, key: string) => {
    props.onChange(key, active);
  };

  return (
    <>
      {widgetList.map(w => (
        <Form.Item label={w.name} valuePropName="checked" key={w.key}>
          <Switch onChange={active => onChange(active, w.key)} defaultChecked={props.widgetsLayout?.some(l => l.i === w.key)} />
        </Form.Item>
      ))}
    </>
  );
}
